import InputField from "components/fields/InputField";
import { useState } from "react";
import { MdPerson, MdEmail, MdUploadFile, MdDelete, MdSend, MdDownload } from "react-icons/md";

const UserInfo = () => {
  const [entryMode, setEntryMode] = useState<"single" | "bulk">("single");
  const [bulkEmails, setBulkEmails] = useState<string>("");
  const [emailList, setEmailList] = useState<Array<{ email: string, firstName?: string, lastName?: string }>>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isSending, setIsSending] = useState(false);

  // Handle CSV file upload
  const handleCSVUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim());

      const emailIndex = headers.findIndex(h => h.includes('email'));
      const firstNameIndex = headers.findIndex(h => h.includes('first') || h.includes('fname'));
      const lastNameIndex = headers.findIndex(h => h.includes('last') || h.includes('lname'));

      const parsedEmails = lines.slice(1)
        .filter(line => line.trim())
        .map(line => {
          const values = line.split(',').map(v => v.trim());
          return {
            email: values[emailIndex] || '',
            firstName: firstNameIndex >= 0 ? values[firstNameIndex] : undefined,
            lastName: lastNameIndex >= 0 ? values[lastNameIndex] : undefined
          };
        })
        .filter(item => item.email && item.email.includes('@'));

      setEmailList(parsedEmails);
    };
    reader.readAsText(file);
    setCsvFile(file);
  };

  // Handle bulk email text input - auto-parse as user types
  const handleBulkEmailChange = (value: string) => {
    setBulkEmails(value);

    // Auto-parse emails as user types
    const emails = value
      .split(/[\n,;]/)
      .map(email => email.trim())
      .filter(email => email && email.includes('@'))
      .map(email => ({ email }));

    setEmailList(emails);
  };

  // Remove email from list
  const removeEmail = (index: number) => {
    setEmailList(prev => prev.filter((_, i) => i !== index));
  };

  // Handle bulk invitation send
  const handleBulkSend = async () => {
    setIsSending(true);
    // Simulate API call
    setTimeout(() => {
      console.log(`Sending invitations to ${emailList.length} users`);
      setIsSending(false);
      // Reset after sending
      setEmailList([]);
      setBulkEmails("");
      setCsvFile(null);
    }, 2000);
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = "Email,First Name,Last Name\njohn.doe@example.com,John,Doe\njane.smith@example.com,Jane,Smith";

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_invitation_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/10">
          <MdPerson className="text-brand-500 text-2xl" />
        </div>
        <div>
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            User Information
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Add new users individually or in bulk
          </p>
        </div>
      </div>

      {/* Entry Mode Selection */}
      <div className="mb-6">
        <label className="mb-3 block text-sm font-medium text-navy-700 dark:text-white">
          Entry Mode
        </label>
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setEntryMode("single")}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${entryMode === "single"
                ? "bg-brand-500 text-white"
                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
              }`}
          >
            Single User
          </button>
          <button
            onClick={() => setEntryMode("bulk")}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${entryMode === "bulk"
                ? "bg-brand-500 text-white"
                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
              }`}
          >
            Bulk Invitation
          </button>
        </div>
      </div>

      {/* Single User Form */}
      {entryMode === "single" && (
        <>
          <div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-2">
            <InputField
              extra="mb-3"
              label="First Name*"
              placeholder="Enter first name"
              id="name"
              type="text"
            />
            <InputField
              extra="mb-3"
              label="Last Name*"
              placeholder="Enter last name"
              id="lastname"
              type="text"
            />
            <InputField
              extra="mb-3 md:col-span-2"
              label="Email Address*"
              placeholder="Enter email address"
              id="email"
              type="email"
            />
          </div>

          {/* Single User Information */}
          <div className="mt-6 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4">
            <div className="flex items-start gap-3">
              <MdEmail className="text-blue-500 text-xl mt-0.5" />
              <div>
                <h6 className="font-semibold text-navy-700 dark:text-white mb-1">
                  Invitation Process
                </h6>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  An invitation email will be sent to the provided email address with instructions
                  to set up their account.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bulk Invitation Form */}
      {entryMode === "bulk" && (
        <>
          {/* Bulk Input Options */}
          <div className="space-y-6">
            {/* CSV Upload */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-navy-700 dark:text-white">
                  Upload CSV File
                </label>
                <button
                  disabled
                  title="Coming Soon"
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 text-sm text-brand-500 hover:text-brand-600 disabled:opacity-50"
                >
                  <MdDownload className="h-4 w-4" />
                  Download Template
                </button>
              </div>
              <div
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file && file.type === 'text/csv') {
                    handleCSVUpload(file);
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                className="relative border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-brand-500 transition-colors"
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleCSVUpload(file);
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <MdUploadFile className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {csvFile ? csvFile.name : "Drop CSV file here or click to browse"}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  CSV should contain: Email, First Name, Last Name • Personalized invitations with recipient's name
                </p>
              </div>
            </div>

            {/* OR Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
              <span className="text-sm text-gray-500 dark:text-gray-400">OR</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
            </div>

            {/* Text Area for Emails */}
            <div>
              <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                Paste Email Addresses
              </label>
              <textarea
                value={bulkEmails}
                onChange={(e) => handleBulkEmailChange(e.target.value)}
                placeholder="Enter email addresses separated by commas, semicolons, or new lines&#10;&#10;Example:&#10;john@example.com&#10;jane@example.com&#10;mike@example.com"
                rows={6}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white"
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Emails are automatically detected as you type
              </p>
            </div>

            {/* Email List Preview */}
            {emailList.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h6 className="font-semibold text-navy-700 dark:text-white">
                    Email List ({emailList.length} user{emailList.length !== 1 ? 's' : ''})
                  </h6>
                  <button
                    onClick={() => {
                      setEmailList([]);
                      setBulkEmails("");
                      setCsvFile(null);
                    }}
                    className="text-sm text-red-500 hover:text-red-600"
                  >
                    Clear All
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  {emailList.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 dark:bg-navy-800 rounded-lg p-2"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {item.email}
                        </p>
                        {(item.firstName || item.lastName) && (
                          <p className="text-xs text-gray-500">
                            {[item.firstName, item.lastName].filter(Boolean).join(' ')}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeEmail(index)}
                        className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                      >
                        <MdDelete className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Send Invitations Button */}
            {emailList.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={handleBulkSend}
                  disabled={isSending}
                  className={`w-full flex items-center justify-center gap-2 rounded-lg px-6 py-3 font-medium transition-all ${isSending
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-brand-500 text-white hover:bg-brand-600"
                    }`}
                >
                  <MdSend className="h-5 w-5" />
                  {isSending
                    ? "Sending..."
                    : `Send ${emailList.length} Invitation${emailList.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            )}
          </div>

          {/* Bulk Information */}
          <div className="mt-6 rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
            <div className="flex items-start gap-3">
              <MdEmail className="text-green-500 text-xl mt-0.5" />
              <div>
                <h6 className="font-semibold text-navy-700 dark:text-white mb-1">
                  Bulk Invitation Process
                </h6>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Invitation emails will be sent to all listed addresses. Each recipient will receive
                  a personalized invitation to join. You can track invitation status
                  in the Users dashboard.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserInfo;